import { _ as __rest } from '../../../tslib.es6-C8rd5pYo.mjs';
import Downshift from 'downshift';
import { css, jsx } from '@emotion/react';
import { useRef } from 'react';
import { Icon } from '../../display/Icon/Icon.es.js';
import { FloatingMenu } from '../FloatingMenu/FloatingMenu.es.js';
import { Input } from '../Input/Input.es.js';
import { Colors } from '../../../theme/Colors.es.js';
import '../../../theme/Shadows.es.js';
import '../../../theme/Typography.es.js';
import '../../../theme/ZinniaStylesReset.es.js';
import { camelCaseAriaAttrs } from '../../../utils/formatAriaAttrKeys.es.js';
import { labelStyles } from '@zapier/style-encapsulation';
import { omit } from '../../../utils/omit.es.js';

const isDisabled = (props) => {
  return props.isDisabled || props.items.length === 0;
};
const Styles = labelStyles("Dropdown", {
  root: css({
    position: "relative"
  }),
  buttonWrap: (props) => ({
    cursor: isDisabled(props) ? "default" : "pointer"
  }),
  button: css({
    maxWidth: "100%",
    overflow: "hidden"
  }),
  buttonText: css({
    display: "block",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    lineHeight: "var(--zds-line-height-xsmall, 20px)"
  }),
  placeholder: css({
    color: `var(--zds-text-weakest, ${Colors.neutral600})`
  })
});
const buildAriaLabel = ({ label, value, placeholder }) => {
  return [value, label, !value && placeholder].filter(Boolean).join(", ");
};
function Dropdown(_a) {
  var { size = "small", getLabelForItem = (item) => item.label, renderFloatingMenu = (props2) => jsx(FloatingMenu, Object.assign({}, props2)), menuAriaLabel = "List of items" } = _a, otherProps = __rest(_a, ["size", "getLabelForItem", "renderFloatingMenu", "menuAriaLabel"]);
  const props = Object.assign({
    size,
    getLabelForItem,
    renderFloatingMenu,
    menuAriaLabel
  }, otherProps);
  const parentRef = useRef(null);
  const itemToString = (item) => {
    return item ? props.getLabelForItem(item) : "";
  };
  const onChangeNoNull = (selection) => {
    var _a2;
    if (selection) {
      (_a2 = props.onChange) === null || _a2 === void 0 ? void 0 : _a2.call(props, selection);
    }
  };
  const onSelectNoNull = (selection) => {
    var _a2;
    if (selection) {
      (_a2 = props.onSelect) === null || _a2 === void 0 ? void 0 : _a2.call(props, selection);
    }
  };
  const stateReducer = (_, changes) => {
    switch (changes.type) {
      case Downshift.stateChangeTypes.keyDownEscape:
        return {
          isOpen: false
        };
      default:
        return changes;
    }
  };
  const renderButtonForItem = props.renderButtonForItem || props.getLabelForItem;
  return jsx(Downshift, { initialSelectedItem: props.initialSelectedItem, inputId: props.inputId, itemCount: props.items.length, itemToString, onChange: onChangeNoNull, onSelect: onSelectNoNull, stateReducer, selectedItem: props.selectedItem }, ({ clearSelection, closeMenu, getInputProps, getItemProps, getMenuProps, getRootProps, highlightedIndex, isOpen, openMenu, selectItem, selectedItem, setHighlightedIndex }) => {
    const hasSelectedItem = !!selectedItem;
    const renderIcon = () => typeof props.renderIcon === "function" ? props.renderIcon(selectedItem) : void 0;
    const rootProps = omit(getRootProps(), ["aria-labelledby"]);
    const onBlur = (e) => {
      if (props.onBlur) {
        const meta = {
          didBlurWithin: parentRef.current ? parentRef.current.contains(e.relatedTarget) : false,
          closeMenu
        };
        props.onBlur(e, meta);
      } else {
        closeMenu();
      }
    };
    const propsNoOnChangeOrOnClick = __rest(props, ["onChange", "onClick"]);
    return jsx(
      "div",
      Object.assign({ "aria-label": props.menuAriaLabel, "aria-labelledby": props.inputId, css: Styles.root }, rootProps, { onBlur, ref: parentRef, "data-zds": true }),
      jsx(
        "div",
        { css: Styles.buttonWrap(props), "data-zds": true },
        jsx(Input, Object.assign({ onClick: () => {
          if (isOpen === true) {
            closeMenu();
          } else if (!isDisabled(props)) {
            openMenu();
          }
          if (props.onClick)
            props.onClick();
        } }, propsNoOnChangeOrOnClick, camelCaseAriaAttrs(getInputProps()), { canClickIconAfter: false, id: props.inputId, isDisabled: isDisabled(props), renderIconAfter: () => jsx(Icon, { name: "arrowDropdown", size: 24 }), renderIconBefore: renderIcon }), (inputProps) => (
          // Intentionally `button` and not `Button` since it looks
          // and behaves differently.
          jsx("button", {
            // `Downshift` requires this attribute to work properly, so
            // there's an a11y balance to perform here. This is spread as
            // an object since the `eslint-disable-next-line` comment didn't
            // work for JSX props.
            // eslint-disable-next-line jsx-a11y/role-supports-aria-props
            "aria-activedescendant": inputProps["aria-activedescendant"],
            "aria-invalid": inputProps["aria-invalid"],
            "aria-controls": inputProps["aria-controls"],
            "aria-expanded": isOpen ? "true" : "false",
            "aria-haspopup": "true",
            "aria-label": buildAriaLabel({
              label: inputProps["aria-label"] || "",
              placeholder: props.placeholder,
              value: hasSelectedItem ? props.getLabelForItem(selectedItem) : void 0
            }),
            css: [inputProps.css, Styles.button],
            disabled: inputProps.disabled,
            id: inputProps.id,
            onBlur,
            onFocus: props.onFocus,
            onKeyDown: inputProps.onKeyDown,
            type: "button",
            "data-zds": true
          }, hasSelectedItem ? jsx("span", { css: Styles.buttonText, "data-zds": true }, renderButtonForItem(selectedItem)) : jsx("span", { css: [Styles.buttonText, Styles.placeholder], "data-zds": true }, props.placeholder))
        ))
      ),
      isOpen && props.renderFloatingMenu({
        ariaLabel: props.menuAriaLabel,
        clearSelection,
        closeMenu,
        items: props.items,
        highlightedIndex,
        getKeyForItem: props.getKeyForItem,
        getLabelForItem: props.getLabelForItem,
        getItemProps,
        getMenuProps,
        getInputProps,
        renderIcon: props.renderIcon,
        selectItem,
        selectedItem,
        setHighlightedIndex
      })
    );
  });
}

export { Dropdown };
//# sourceMappingURL=Dropdown.es.js.map
